import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';
import { CodeBlock } from '../converter-layout/code-block';

type SchemaType = 'Article' | 'Product' | 'FAQPage' | 'BreadcrumbList' | 'LocalBusiness' | 'Recipe' | 'Event' | 'Organization' | 'HowTo' | 'VideoObject';

interface FieldDef {
    key: string;
    label: string;
    type: 'text' | 'url' | 'date' | 'number' | 'textarea';
    optional?: boolean;
}

const SCHEMA_FIELDS: Record<SchemaType, FieldDef[]> = {
    Article: [
        { key: 'headline', label: 'Headline (max 110 chars)', type: 'text' },
        { key: 'image', label: 'Image URL', type: 'url' },
        { key: 'datePublished', label: 'Date published', type: 'date' },
        { key: 'dateModified', label: 'Date modified', type: 'date', optional: true },
        { key: 'authorName', label: 'Author name', type: 'text' },
        { key: 'publisherName', label: 'Publisher name', type: 'text' },
        { key: 'publisherLogo', label: 'Publisher logo URL', type: 'url' },
        { key: 'description', label: 'Description', type: 'textarea', optional: true },
    ],
    Product: [
        { key: 'name', label: 'Product name', type: 'text' },
        { key: 'image', label: 'Image URL', type: 'url' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'sku', label: 'SKU', type: 'text', optional: true },
        { key: 'brand', label: 'Brand name', type: 'text', optional: true },
        { key: 'priceCurrency', label: 'Currency (e.g. USD)', type: 'text' },
        { key: 'price', label: 'Price', type: 'number' },
        { key: 'ratingValue', label: 'Rating (1-5)', type: 'number', optional: true },
        { key: 'reviewCount', label: 'Review count', type: 'number', optional: true },
    ],
    FAQPage: [
        { key: 'faqs', label: 'FAQs (one per line, format: "Q? | A.")', type: 'textarea' },
    ],
    BreadcrumbList: [
        { key: 'breadcrumbs', label: 'Breadcrumbs (one per line, format: "Name | URL")', type: 'textarea' },
    ],
    LocalBusiness: [
        { key: 'name', label: 'Business name', type: 'text' },
        { key: 'address', label: 'Street address', type: 'text' },
        { key: 'city', label: 'City', type: 'text' },
        { key: 'region', label: 'State / Region', type: 'text' },
        { key: 'postalCode', label: 'Postal code', type: 'text' },
        { key: 'country', label: 'Country (e.g. US)', type: 'text' },
        { key: 'telephone', label: 'Phone', type: 'text', optional: true },
        { key: 'url', label: 'Website URL', type: 'url', optional: true },
    ],
    Recipe: [
        { key: 'name', label: 'Recipe name', type: 'text' },
        { key: 'image', label: 'Image URL', type: 'url' },
        { key: 'authorName', label: 'Author', type: 'text' },
        { key: 'prepTime', label: 'Prep time (ISO 8601, e.g. PT15M)', type: 'text' },
        { key: 'cookTime', label: 'Cook time (ISO 8601)', type: 'text' },
        { key: 'recipeYield', label: 'Yield (e.g. 4 servings)', type: 'text' },
        { key: 'ingredients', label: 'Ingredients (one per line)', type: 'textarea' },
        { key: 'instructions', label: 'Instructions (one per line)', type: 'textarea' },
    ],
    Event: [
        { key: 'name', label: 'Event name', type: 'text' },
        { key: 'startDate', label: 'Start date/time (ISO 8601)', type: 'text' },
        { key: 'endDate', label: 'End date/time (ISO 8601)', type: 'text', optional: true },
        { key: 'location', label: 'Location name', type: 'text' },
        { key: 'locationAddress', label: 'Location address', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea', optional: true },
        { key: 'image', label: 'Image URL', type: 'url', optional: true },
    ],
    Organization: [
        { key: 'name', label: 'Organization name', type: 'text' },
        { key: 'url', label: 'Website URL', type: 'url' },
        { key: 'logo', label: 'Logo URL', type: 'url' },
        { key: 'sameAs', label: 'Same-as URLs (one per line)', type: 'textarea', optional: true },
    ],
    HowTo: [
        { key: 'name', label: 'How-to title', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea', optional: true },
        { key: 'totalTime', label: 'Total time (ISO 8601, e.g. PT30M)', type: 'text', optional: true },
        { key: 'steps', label: 'Steps (one per line, format: "Step name | Step text")', type: 'textarea' },
    ],
    VideoObject: [
        { key: 'name', label: 'Video title', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea' },
        { key: 'thumbnailUrl', label: 'Thumbnail URL', type: 'url' },
        { key: 'uploadDate', label: 'Upload date', type: 'date' },
        { key: 'duration', label: 'Duration (ISO 8601, e.g. PT2M30S)', type: 'text', optional: true },
        { key: 'contentUrl', label: 'Content URL', type: 'url', optional: true },
        { key: 'embedUrl', label: 'Embed URL', type: 'url', optional: true },
    ],
};

interface SchemaState {
    schemaType: SchemaType;
    fields: Record<string, string>;
    copied: boolean;
}

export default class JsonLdSchemaGeneratorComponent extends Component<{ feature?: string }, SchemaState> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: SchemaState = { schemaType: 'Article', fields: {}, copied: false };

    private buildSchema(): object {
        const { schemaType, fields } = this.state;
        const ctx = { '@context': 'https://schema.org', '@type': schemaType };

        const linesToObjects = (raw: string, sep: '|', mapper: (parts: string[], i: number) => any) =>
            raw.split('\n').map(l => l.trim()).filter(Boolean).map((l, i) => mapper(l.split(sep).map(s => s.trim()), i)).filter(Boolean);

        switch (schemaType) {
            case 'Article': return {
                ...ctx,
                headline: fields.headline || undefined,
                image: fields.image || undefined,
                datePublished: fields.datePublished || undefined,
                dateModified: fields.dateModified || undefined,
                author: fields.authorName ? { '@type': 'Person', name: fields.authorName } : undefined,
                publisher: fields.publisherName ? {
                    '@type': 'Organization',
                    name: fields.publisherName,
                    logo: fields.publisherLogo ? { '@type': 'ImageObject', url: fields.publisherLogo } : undefined,
                } : undefined,
                description: fields.description || undefined,
            };
            case 'Product': return {
                ...ctx,
                name: fields.name || undefined,
                image: fields.image || undefined,
                description: fields.description || undefined,
                sku: fields.sku || undefined,
                brand: fields.brand ? { '@type': 'Brand', name: fields.brand } : undefined,
                offers: fields.price ? {
                    '@type': 'Offer',
                    priceCurrency: fields.priceCurrency || 'USD',
                    price: fields.price,
                } : undefined,
                aggregateRating: fields.ratingValue ? {
                    '@type': 'AggregateRating',
                    ratingValue: fields.ratingValue,
                    reviewCount: fields.reviewCount || '1',
                } : undefined,
            };
            case 'FAQPage':
                return {
                    ...ctx,
                    mainEntity: linesToObjects(fields.faqs || '', '|', ([q, a]) => q && a ? {
                        '@type': 'Question',
                        name: q,
                        acceptedAnswer: { '@type': 'Answer', text: a },
                    } : null),
                };
            case 'BreadcrumbList':
                return {
                    ...ctx,
                    itemListElement: linesToObjects(fields.breadcrumbs || '', '|', ([name, url], i) => name ? {
                        '@type': 'ListItem',
                        position: i + 1,
                        name,
                        item: url || undefined,
                    } : null).map((item: any, i: number) => ({ ...item, position: i + 1 })),
                };
            case 'LocalBusiness': return {
                ...ctx,
                name: fields.name || undefined,
                address: {
                    '@type': 'PostalAddress',
                    streetAddress: fields.address || undefined,
                    addressLocality: fields.city || undefined,
                    addressRegion: fields.region || undefined,
                    postalCode: fields.postalCode || undefined,
                    addressCountry: fields.country || undefined,
                },
                telephone: fields.telephone || undefined,
                url: fields.url || undefined,
            };
            case 'Recipe': return {
                ...ctx,
                name: fields.name || undefined,
                image: fields.image || undefined,
                author: fields.authorName ? { '@type': 'Person', name: fields.authorName } : undefined,
                prepTime: fields.prepTime || undefined,
                cookTime: fields.cookTime || undefined,
                recipeYield: fields.recipeYield || undefined,
                recipeIngredient: (fields.ingredients || '').split('\n').map(s => s.trim()).filter(Boolean),
                recipeInstructions: (fields.instructions || '').split('\n').map(s => s.trim()).filter(Boolean).map(text => ({ '@type': 'HowToStep', text })),
            };
            case 'Event': return {
                ...ctx,
                name: fields.name || undefined,
                startDate: fields.startDate || undefined,
                endDate: fields.endDate || undefined,
                location: fields.location ? {
                    '@type': 'Place',
                    name: fields.location,
                    address: fields.locationAddress || undefined,
                } : undefined,
                description: fields.description || undefined,
                image: fields.image || undefined,
            };
            case 'Organization': return {
                ...ctx,
                name: fields.name || undefined,
                url: fields.url || undefined,
                logo: fields.logo || undefined,
                sameAs: (fields.sameAs || '').split('\n').map(s => s.trim()).filter(Boolean),
            };
            case 'HowTo': return {
                ...ctx,
                name: fields.name || undefined,
                description: fields.description || undefined,
                totalTime: fields.totalTime || undefined,
                step: linesToObjects(fields.steps || '', '|', ([name, text]) => name ? {
                    '@type': 'HowToStep',
                    name: name,
                    text: text || name,
                } : null),
            };
            case 'VideoObject': return {
                ...ctx,
                name: fields.name || undefined,
                description: fields.description || undefined,
                thumbnailUrl: fields.thumbnailUrl || undefined,
                uploadDate: fields.uploadDate || undefined,
                duration: fields.duration || undefined,
                contentUrl: fields.contentUrl || undefined,
                embedUrl: fields.embedUrl || undefined,
            };
        }
    }

    private clean(obj: any): any {
        if (Array.isArray(obj)) return obj.map(v => this.clean(v)).filter(v => v !== undefined && v !== null && (typeof v !== 'object' || Object.keys(v).length > 0));
        if (obj && typeof obj === 'object') {
            const out: any = {};
            for (const k of Object.keys(obj)) {
                const v = this.clean(obj[k]);
                if (v === undefined || v === null || (Array.isArray(v) && v.length === 0)) continue;
                if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) continue;
                out[k] = v;
            }
            return out;
        }
        return obj;
    }

    private generate(): string {
        const clean = this.clean(this.buildSchema());
        const json = JSON.stringify(clean, null, 2);
        return `<script type="application/ld+json">\n${json}\n</script>`;
    }

    private setField = (key: string, value: string) => {
        this.setState({ fields: { ...this.state.fields, [key]: value } });
    };

    private changeType = (t: SchemaType) => {
        this.setState({ schemaType: t, fields: {} });
    };

    copyOutput = () => {
        navigator.clipboard.writeText(this.generate()).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private validate(): string | null {
        const { schemaType, fields } = this.state;
        const nonEmpty = (s?: string) => !!(s && s.trim());
        const lines = (s?: string) => (s || '').split('\n').map(l => l.trim()).filter(Boolean);
        switch (schemaType) {
            case 'FAQPage':
                if (lines(fields.faqs).length === 0) return 'FAQPage requires at least one Q&A entry. Format: "Question? | Answer."';
                return null;
            case 'BreadcrumbList':
                if (lines(fields.breadcrumbs).length === 0) return 'BreadcrumbList requires at least one item. Format: "Name | https://url"';
                return null;
            case 'Article':
                if (!nonEmpty(fields.headline)) return 'Article requires a headline for Google rich-result validation.';
                return null;
            case 'Product':
                if (!nonEmpty(fields.name)) return 'Product requires a name for Google rich-result validation.';
                return null;
            default:
                return null;
        }
    }

    render() {
        const { schemaType, fields, copied } = this.state;
        const defs = SCHEMA_FIELDS[schemaType];
        const output = this.generate();
        const lineCount = output.split('\n').length;
        const validationError = this.validate();

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Schema type</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="schema-type">schema.org type</label>
                        <select id="schema-type" value={schemaType} onChange={e => this.changeType(e.target.value as SchemaType)}>
                            {Object.keys(SCHEMA_FIELDS).map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                </div>

                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>{schemaType} fields</span></div>
                    {defs.map(f => (
                        <div key={f.key} className={genStyles.field}>
                            <label htmlFor={`schema-${f.key}`}>
                                {f.label}{f.optional && <span style={{ color: '#94a3b8', fontWeight: 400 }}> (optional)</span>}
                            </label>
                            {f.type === 'textarea' ? (
                                <textarea id={`schema-${f.key}`} rows={4} value={fields[f.key] || ''}
                                    onChange={e => this.setField(f.key, e.target.value)} />
                            ) : (
                                <input id={`schema-${f.key}`} type={f.type} value={fields[f.key] || ''}
                                    onChange={e => this.setField(f.key, e.target.value)} />
                            )}
                        </div>
                    ))}
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Generated JSON-LD"
                previewMeta={`${lineCount} line${lineCount === 1 ? '' : 's'}`}
                preview={<CodeBlock code={output} language="html" wrapLines maxHeight={500} minHeight={320} ariaLabel="Generated JSON-LD schema" />}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput}>{copied ? '✓ Copied' : '📋 Copy'}</button>
                }
                error={validationError}
            />
        );
    }
}
